/**
 * TankBatteriesTab — Phase 24 / Task 24.3 (DEC-094).
 * List of tank batteries → click into the detail panel (wells / tanks / audit).
 */
import * as React from 'react';
import { useTankBatteries, useHaulerOperators, type TankBatterySummary } from './hooks';
import { NewLocationModal } from './NewLocationModal';
import { TankBatteryDetail } from './TankBatteryDetail';
import {
  ActiveBadge,
  AddButton,
  Pill,
  SectionHeader,
  rowStyle,
  searchInputStyle,
  subText,
} from './ui-bits';
import { formatNumber } from '@lodex-pro/shared/number-format';

export function TankBatteriesTab({ canManage }: { canManage: boolean }): React.JSX.Element {
  const { data, isLoading, isError } = useTankBatteries();
  const { data: opsData } = useHaulerOperators();
  const [filter, setFilter] = React.useState('');
  const [newOpen, setNewOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const batteries = data?.tankBatteries ?? [];
  const operators = opsData?.haulerOperators ?? [];

  const selected = selectedId !== null ? batteries.find((b) => b.id === selectedId) : undefined;

  if (selected !== undefined) {
    const haulerOp = operators.find((o) => o.id === selected.haulerOperatorId);
    return (
      <TankBatteryDetail
        battery={selected}
        operatorPlatformId={haulerOp?.operatorId ?? null}
        canManage={canManage}
        onClose={(): void => setSelectedId(null)}
      />
    );
  }

  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? batteries.filter(
        (b) =>
          b.name.toLowerCase().includes(needle) || b.operatorName.toLowerCase().includes(needle),
      )
    : batteries;

  return (
    <div>
      <SectionHeader
        title="Sites"
        count={batteries.length}
        action={
          canManage ? (
            <AddButton label="New Location" onClick={(): void => setNewOpen(true)} />
          ) : null
        }
      />
      <input
        value={filter}
        onChange={(e): void => setFilter(e.target.value)}
        placeholder="Filter sites…"
        style={searchInputStyle}
        aria-label="Filter sites"
      />

      {isLoading && <p style={subText}>Loading…</p>}
      {isError && <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load sites.</p>}
      {!isLoading && visible.length === 0 && <p style={subText}>No sites yet.</p>}

      {visible.map((b: TankBatterySummary) => (
        <div
          key={b.id}
          style={{ ...rowStyle, cursor: 'pointer', opacity: b.isActive ? 1 : 0.55 }}
          onClick={(): void => setSelectedId(b.id)}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 600 }}>{b.name}</span>
              {b.isPrivate && <Pill label="Private" tone="muted" />}
            </div>
            <div style={subText}>
              {b.operatorName} ({b.operatorAbbreviation}) · {formatNumber(b.wellCount)} well
              {b.wellCount === 1 ? '' : 's'} · {formatNumber(b.tankCount)} tank
              {b.tankCount === 1 ? '' : 's'}
            </div>
          </div>
          <ActiveBadge active={b.isActive} />
        </div>
      ))}

      <NewLocationModal open={newOpen} onOpenChange={setNewOpen} operators={operators} />
    </div>
  );
}
